import React, { useState, useMemo } from 'react';
import Appear from '../components/ui/Appear';
import { buttonClass } from '../components/ui/Button';
import useScheduleStore from '../stores/useScheduleStore';
import { buildEventColorMap } from './schedule-colors';
import DayModeToggle from '../components/DayModeToggle';
import Tooltip from '../components/ui/Tooltip';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface ScheduleHeaderProps {
  collapsedEmpty: boolean;
  onToggleCollapse: () => void;
  hasEmptyDays: boolean;
  showUnavailableBlocks: boolean;
  onToggleShowBlocks: () => void;
  /** Scrolls the grid back to today's column. Does NOT refetch. */
  onJumpToToday: () => void;
}

const ScheduleHeader: React.FC<ScheduleHeaderProps> = ({
  collapsedEmpty,
  onToggleCollapse,
  hasEmptyDays,
  showUnavailableBlocks,
  onToggleShowBlocks,
  onJumpToToday,
}) => {
  const { rangeStart, rangeEnd, events } = useScheduleStore();
  const [legendExpanded, setLegendExpanded] = useState(false);

  const eventColorMap = useMemo(() => buildEventColorMap(events), [events]);

  // The ◀▶ pager is gone (item 9): the window is continuous, so moving through
  // time is a scroll, not a fetch. That also removes the "discard unsaved
  // changes?" confirm this header used to need — scrolling cannot lose work,
  // and the prompt was firing on what users read as a harmless navigation.
  const windowLabel = useMemo(() => {
    const fmt = (ds: string) => {
      const [y, m] = ds.split('-').map(Number);
      return `${MONTH_NAMES[m - 1].slice(0, 3)} ${y}`;
    };
    const from = fmt(rangeStart);
    const to = fmt(rangeEnd);
    return from === to ? from : `${from} to ${to}`;
  }, [rangeStart, rangeEnd]);

  return (
    <div className="flex flex-col gap-2 mb-4">
      <div className="flex items-center justify-between">
        {/* Window label + jump-to-today. Scrolling replaces paging. */}
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-on-surface day:text-gray-900">
            Schedule
          </h2>
          <span className="text-xs text-on-surface-variant day:text-gray-600">{windowLabel}</span>
          <button
            onClick={onJumpToToday}
            className={buttonClass('tonal', 'sm')}
          >
            Today
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <Tooltip label={showUnavailableBlocks ? 'Hide unavailability blocks' : 'Show unavailability blocks'}><button
            onClick={onToggleShowBlocks}
            className={`px-3 py-1.5 text-xs rounded border transition-colors flex items-center gap-1.5
              ${showUnavailableBlocks
                ? 'border-slate-500/60 day:border-slate-400 text-slate-200 day:text-slate-700 bg-slate-700/30 day:bg-slate-200 hover:bg-slate-700/40 day:hover:bg-slate-300'
                : 'border-gray-700 day:border-gray-300 text-on-surface-variant day:text-gray-700 hover:text-on-surface day:hover:text-gray-900 hover:border-outline day:hover:border-gray-400'}`}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 12L12 2M5 2H12V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {showUnavailableBlocks ? 'Blocks on' : 'Blocks off'}
          </button></Tooltip>
          {hasEmptyDays && (
            <button
              onClick={onToggleCollapse}
              className={`px-3 py-1.5 text-xs rounded border transition-colors flex items-center gap-1.5
                ${collapsedEmpty
                  ? 'border-primary/40 day:border-amber-500 text-primary-ink day:text-amber-700 bg-primary/5 day:bg-amber-50 hover:bg-primary/10 day:hover:bg-amber-100'
                  : 'border-gray-700 day:border-gray-300 text-on-surface-variant day:text-gray-700 hover:text-on-surface day:hover:text-gray-900 hover:border-outline day:hover:border-gray-400'}`}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 5.5H11M3 8.5H11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                {collapsedEmpty && <path d="M7 3V11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>}
              </svg>
              {collapsedEmpty ? 'Show all days' : 'Collapse empty'}
            </button>
          )}
          <DayModeToggle />
        </div>
      </div>

      {/* Event color legend */}
      {events.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <button
            onClick={() => setLegendExpanded(!legendExpanded)}
            className="text-xs text-on-surface-variant day:text-gray-600 hover:text-on-surface day:hover:text-gray-900 mr-1"
          >
            {legendExpanded ? '▾' : '▸'} Legend ({events.length})
          </button>
          {(legendExpanded ? events : events.slice(0, 6)).map(evt => {
            const color = eventColorMap.get(evt.id);
            return (
              <span
                key={evt.id}
                className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium
                  ${color?.bg || ''} ${color?.text || 'text-on-surface day:text-gray-700'} border ${color?.border || 'border-outline-variant day:border-gray-300'}`}
              >
                {evt.name}
              </span>
            );
          })}
          <Appear show={!legendExpanded && events.length > 6} className="text-[10px] text-on-surface-variant day:text-gray-600">+{events.length - 6} more</Appear>
        </div>
      )}
    </div>
  );
};

export default ScheduleHeader;
